import { useState, useEffect, useCallback, createContext, useContext, useRef, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { RefreshCw } from "lucide-react";

import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CreateTeam = lazy(() => import("@/pages/CreateTeam"));
const TeamRoster = lazy(() => import("@/pages/TeamRoster"));
const SkillReference = lazy(() => import("@/pages/SkillReference"));
const LeagueList = lazy(() => import("@/pages/LeagueList"));
const LeaguePage = lazy(() => import("@/pages/LeaguePage"));
const SharedTeamView = lazy(() => import("@/pages/SharedTeamView"));
const StarPlayers = lazy(() => import("@/pages/StarPlayers"));
const TournamentList = lazy(() => import("@/pages/TournamentList"));
const TournamentPage = lazy(() => import("@/pages/TournamentPage"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios - DO NOT use withCredentials (Safari ITP blocks cross-site cookies)
// Authentication is handled exclusively via Bearer token in localStorage

// Add Bearer token to every request if available
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("bbcoach_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export { API };

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleAuthResponse = useCallback((data) => {
    if (data?.session_token) {
      localStorage.setItem("bbcoach_token", data.session_token);
    }
    const { session_token: _st, ...userData } = data || {};
    if (userData.user_id) {
      setUser(userData);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("bbcoach_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      if (res.data?.user_id) {
        setUser(res.data);
      } else {
        localStorage.removeItem("bbcoach_token");
        setUser(null);
      }
    } catch {
      localStorage.removeItem("bbcoach_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch { /* ignore */ }
    localStorage.removeItem("bbcoach_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: handleAuthResponse, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function DeployBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const initialVersion = useRef(null);
  const { logout } = useAuth();

  useEffect(() => {
    let interval;
    const checkVersion = async () => {
      try {
        const res = await axios.get(`${API}/version`);
        const v = res.data.version;
        if (!initialVersion.current) {
          initialVersion.current = v;
        } else if (v !== initialVersion.current) {
          setShowBanner(true);
        }
      } catch {}
    };
    checkVersion();
    interval = setInterval(checkVersion, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-600 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg shadow-amber-900/30" data-testid="deploy-banner">
      <RefreshCw className="w-4 h-4 animate-spin" />
      <p className="text-xs sm:text-sm font-bold">Nuovo aggiornamento disponibile!</p>
      <button
        onClick={() => { logout(); window.location.href = "/login"; }}
        className="bg-white text-amber-700 text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-sm hover:bg-amber-50 transition-colors"
        data-testid="deploy-logout-btn"
      >
        Aggiorna Ora
      </button>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <><DeployBanner />{children}</>;
}

const PageSpinner = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function AppRouter() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/create-team" element={<ProtectedRoute><Layout><CreateTeam /></Layout></ProtectedRoute>} />
        <Route path="/team/:teamId" element={<ProtectedRoute><Layout><TeamRoster /></Layout></ProtectedRoute>} />
        <Route path="/skills" element={<ProtectedRoute><Layout><SkillReference /></Layout></ProtectedRoute>} />
        <Route path="/leagues" element={<ProtectedRoute><Layout><LeagueList /></Layout></ProtectedRoute>} />
        <Route path="/league/:leagueId" element={<ProtectedRoute><Layout><LeaguePage /></Layout></ProtectedRoute>} />
        <Route path="/shared/team/:teamId" element={<SharedTeamView />} />
        <Route path="/star-players" element={<ProtectedRoute><Layout><StarPlayers /></Layout></ProtectedRoute>} />
        <Route path="/tournaments" element={<ProtectedRoute><Layout><TournamentList /></Layout></ProtectedRoute>} />
        <Route path="/tournament/:tournamentId" element={<ProtectedRoute><Layout><TournamentPage /></Layout></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="grain-overlay">
          <AppRouter />
          <Toaster richColors position="top-center" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
